import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, day, useCurrentCompany, useMe } from '../../lib'
import { ErrorBox, Loading, NoCompany } from '../../app/ui'
import { PageBand } from '../../app/PageBand'
import type { Role, Team } from './types'

export function TeamPage() {
  const { me } = useMe()
  const company = useCurrentCompany()
  const qc = useQueryClient()
  const key = ['accounts', 'team', company?.id]
  const q = useQuery({ queryKey: key, queryFn: () => api<Team>('GET', `/api/companies/${company!.id}/members`), enabled: !!company })
  const done = { onSuccess: () => qc.invalidateQueries({ queryKey: key }) }
  const setRole = useMutation({
    mutationFn: (v: { mid: string; role: Role }) => api('PATCH', `/api/companies/${company!.id}/members/${v.mid}`, { role: v.role }),
    ...done,
  })
  const remove = useMutation({ mutationFn: (mid: string) => api('DELETE', `/api/companies/${company!.id}/members/${mid}`), ...done })
  const revoke = useMutation({ mutationFn: (iid: string) => api('DELETE', `/api/companies/${company!.id}/invites/${iid}`), ...done })
  const decide = useMutation({
    mutationFn: (v: { id: string; approve: boolean }) => api('POST', `/api/join-requests/${v.id}/decide`, { approve: v.approve }),
    ...done,
  })
  const [email, setEmail] = useState('')
  const [role, setNewRole] = useState<Role>('member')
  const invite = useMutation({
    mutationFn: () => api('POST', `/api/companies/${company!.id}/invites`, { email, role }),
    onSuccess: () => {
      setEmail('')
      qc.invalidateQueries({ queryKey: key })
    },
  })

  if (!company) return <NoCompany />
  if (q.isLoading || !q.data) return q.error ? <ErrorBox error={q.error} /> : <Loading />
  const t = q.data
  const admin = t.my_role === 'admin'
  const err = setRole.error ?? remove.error ?? decide.error

  return (
    <div className="stack" data-testid="accounts-team">
      {/* CHANGE-049: the line is about points, which the card-only app does not show */}
      <PageBand title="Team" sub={me?.features?.card_only_app ? undefined : 'Members see points. Admins choose where they pay back.'} />
      {err ? <ErrorBox error={err} /> : null}
      <section className="panel stack">
        <h2>Members</h2>
        <div className="table-wrap">
          <table className="stack-narrow">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Since</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {t.members.map((m) => (
                <tr key={m.id} data-testid="accounts-member-row">
                  <td>{m.email}</td>
                  <td>
                    {admin ? (
                      <select
                        value={m.role}
                        aria-label={`Role for ${m.email}`}
                        data-testid="accounts-member-role"
                        onChange={(e) => setRole.mutate({ mid: m.id, role: e.target.value as Role })}
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                      </select>
                    ) : (
                      <span data-testid="accounts-member-role">{m.role === 'admin' ? 'Admin' : 'Member'}</span>
                    )}
                  </td>
                  <td data-label="Since">{day(m.created_at)}</td>
                  <td>
                    {admin || m.user_id === me?.user?.id ? (
                      <button className="link-button" onClick={() => remove.mutate(m.id)} data-testid="accounts-member-remove">
                        {m.user_id === me?.user?.id ? 'Leave' : 'Remove'}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {admin ? (
        <section className="panel stack">
          <h2>Invite</h2>
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault()
              invite.mutate()
            }}
          >
            <input type="email" required placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="accounts-invite-email" />
            <select value={role} onChange={(e) => setNewRole(e.target.value as Role)} aria-label="Invite role" data-testid="accounts-invite-role">
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button className="button primary" type="submit" disabled={invite.isPending} data-testid="accounts-invite-send">
              Send invite
            </button>
          </form>
          {invite.error ? <ErrorBox error={invite.error} /> : null}
          {t.invites.length ? (
            <>
              <h3>Invited</h3>
              {revoke.error ? <ErrorBox error={revoke.error} /> : null}
              <ul data-testid="accounts-open-invites">
                {t.invites.map((i) => (
                  <li key={i.id} data-testid="accounts-open-invite">
                    {i.email} ({i.role === 'admin' ? 'Admin' : 'Member'}), until {day(i.expires_at)}{' '}
                    <button
                      className="link-button"
                      onClick={() => revoke.mutate(i.id)}
                      disabled={revoke.isPending}
                      aria-label={`Revoke invite for ${i.email}`}
                      data-testid="accounts-invite-revoke"
                    >
                      Revoke
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      ) : null}

      {admin && t.join_requests.length ? (
        <section className="panel stack" data-testid="accounts-join-requests">
          <h2>Asking to join</h2>
          {t.join_requests.map((j) => (
            <div key={j.id} className="row">
              <span>{j.email}</span>
              <span className="spacer" />
              <button className="button primary" onClick={() => decide.mutate({ id: j.id, approve: true })} data-testid="accounts-join-approve">
                Let in
              </button>
              <button className="button" onClick={() => decide.mutate({ id: j.id, approve: false })} data-testid="accounts-join-deny">
                Say no
              </button>
            </div>
          ))}
        </section>
      ) : null}
    </div>
  )
}
